import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Typography, Spin, message, Button } from 'antd';
import {
  BankOutlined,
  TeamOutlined,
  DollarOutlined,
  WarningOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  DownOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import './SuperAdminDashboard.css';

const { Title } = Typography;

const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    inactiveCompanies: 0,
    totalEmployees: 0,
    expiringCompanies: 0,
    totalRevenue: 0
  });
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [userName, setUserName] = useState('Admin');

  useEffect(() => {
    // Get user name from localStorage or session
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.name) {
      setUserName(user.name);
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/dashboard');
      if (response.data.success) {
        setStats(response.data.data.stats);
        setRecentCompanies(response.data.data.recentCompanies || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use default stats if API fails
      message.warning('Could not load live data. Showing defaults.');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Companies',
      value: stats.totalCompanies,
      icon: <BankOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      color: '#e6f7ff',
      borderColor: '#1890ff',
      change: stats.totalCompanies > 0 ? '+12.5%' : '0%',
      isPositive: true
    },
    {
      title: 'Active Companies',
      value: stats.activeCompanies,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      color: '#f6ffed',
      borderColor: '#52c41a',
      change: stats.activeCompanies > 0 ? '+13.6%' : '0%',
      isPositive: true
    },
    {
      title: 'Total Employees',
      value: stats.totalEmployees,
      icon: <TeamOutlined style={{ fontSize: 24, color: '#722ed1' }} />,
      color: '#f9f0ff',
      borderColor: '#722ed1',
      change: stats.totalEmployees > 0 ? '+8.2%' : '0%',
      isPositive: true
    },
    {
      title: 'Total Revenue',
      value: stats.totalRevenue,
      prefix: '₹',
      icon: <DollarOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      color: '#e6fffb',
      borderColor: '#13c2c2',
      change: stats.totalRevenue > 0 ? '+15%' : '0%',
      isPositive: true
    },
    {
      title: 'Expiring Soon',
      value: stats.expiringCompanies,
      icon: <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />,
      color: '#fffbe6',
      borderColor: '#faad14',
      change: stats.expiringCompanies > 0 ? '-4%' : '0%',
      isPositive: false
    },
    {
      title: 'Growth Rate',
      value: stats.activeCompanies > 0 ? Math.round((stats.activeCompanies / Math.max(stats.totalCompanies, 1)) * 100) : 0,
      suffix: '%',
      icon: <RiseOutlined style={{ fontSize: 24, color: '#eb2f96' }} />,
      color: '#fff0f6',
      borderColor: '#eb2f96',
      change: '+5.2%',
      isPositive: true
    }
  ];

  const recentColumns = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: 'Client',
      dataIndex: 'clientName',
      key: 'clientName',
    },
    {
      title: 'Package',
      dataIndex: ['Package', 'name'],
      key: 'package',
      render: (text) => text || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          active: 'green',
          inactive: 'orange',
          expired: 'red',
          suspended: 'purple'
        };
        return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString('en-IN') : 'N/A'
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ backgroundColor: '#f5f7fa', minHeight: '100vh', padding: '20px' }}>
      {/* Header Section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div style={{ color: '#6b7280', fontSize: '13px' }}>
          Super Admin › Dashboard
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button icon={<CalendarOutlined />} size="small" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/')}
            <DownOutlined style={{ fontSize: '10px' }} />
          </Button>
          <Button type="primary" size="small" style={{ backgroundColor: '#1f2937', fontSize: '12px' }}>
            Compare
          </Button>
          <Button size="small" style={{ fontSize: '12px' }}>
            All Packages
          </Button>
        </div>
      </div>

      {/* Welcome Banner */}
      <Card
        style={{
          background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c5a 100%)',
          borderRadius: '8px',
          marginBottom: '20px',
          border: 'none',
          position: 'relative',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ 
            color: 'white', 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0 0 6px 0' 
          }}>
            Welcome Back, {userName}
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '14px' }}>
            {stats.activeCompanies} New Companies Subscribed Today !
          </p>
        </div>
        <div style={{
          position: 'absolute',
          right: '24px',
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: 0.2,
          fontSize: '48px'
        }}>
          🎯
        </div>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        {statCards.map((card, index) => (
          <Col xs={24} sm={12} md={8} lg={4} key={index}>
            <Card
              style={{
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                height: '100%',
                background: '#fff'
              }}
              bodyStyle={{ padding: '20px 16px 12px 16px' }}
            >
              {/* Icon and Badge Row */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: card.borderColor,
                  fontSize: '20px'
                }}>
                  {card.icon}
                </div>
                <span style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: card.isPositive ? '#e8f5e9' : '#ffebee',
                  color: card.isPositive ? '#2e7d32' : '#c62828',
                  fontWeight: '600',
                  lineHeight: '1'
                }}>
                  {card.change}
                </span>
              </div>

              {/* Value */}
              <div style={{ 
                fontSize: '28px', 
                fontWeight: '700',
                color: '#1f2937',
                marginBottom: '4px',
                lineHeight: '1'
              }}>
                {card.prefix || ''}{card.value.toLocaleString()}{card.suffix || ''}
              </div>

              {/* Title */}
              <div style={{ 
                fontSize: '13px', 
                color: '#6b7280',
                marginBottom: '12px',
                fontWeight: '400'
              }}>
                {card.title}
              </div>

              {/* Mini Chart */}
              <div style={{ height: '36px', marginTop: 'auto' }}>
                <MiniChart type={index % 4} />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Content Grid */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {/* Companies Chart */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Companies</span>
                <Button size="small" icon={<DownOutlined />} style={{ fontSize: '11px' }}>
                  This Week
                </Button>
              </div>
            }
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: 'none'
            }}
            bodyStyle={{ paddingTop: '12px' }}
          >
            <CompaniesBarChart />
            <div style={{ 
              marginTop: '12px',
              padding: '8px',
              backgroundColor: '#f0fdf4',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#166534'
            }}>
              <span style={{ color: '#10b981', fontWeight: '600' }}>
                {stats.totalCompanies > 0 ? '+19%' : '0%'}
              </span> {stats.totalCompanies} Companies from last month
            </div>
          </Card>
        </Col>

        {/* Revenue Chart */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Revenue</span>
                <Button size="small" icon={<DownOutlined />} style={{ fontSize: '11px' }}>
                  2026
                </Button>
              </div>
            }
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: 'none'
            }}
            bodyStyle={{ paddingTop: '12px' }}
          >
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                ₹{stats.totalRevenue ? stats.totalRevenue.toLocaleString() : '0'}
              </div>
              <div style={{ fontSize: '12px', color: '#10b981' }}>
                <ArrowUpOutlined /> {stats.totalRevenue > 0 ? '+8.6%' : '0%'} compared from last year
              </div>
            </div>
            <RevenueChart stats={stats} />
          </Card>
        </Col>

        {/* Top Plans */}
        <Col xs={24} lg={6}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Top Plans</span>
                <Button size="small" icon={<DownOutlined />} style={{ fontSize: '11px' }}>
                  This Month
                </Button>
              </div>
            }
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: 'none'
            }}
            bodyStyle={{ paddingTop: '12px' }}
          >
            <TopPlansDoughnut />
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <PlanLegend 
                color="#3b82f6" 
                label="Basic" 
                percentage={stats.totalCompanies > 0 ? '60%' : '0%'} 
              />
              <PlanLegend 
                color="#f97316" 
                label="Premium" 
                percentage={stats.totalCompanies > 0 ? '20%' : '0%'} 
              />
              <PlanLegend 
                color="#eab308" 
                label="Enterprise" 
                percentage={stats.totalCompanies > 0 ? '20%' : '0%'} 
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Companies Table */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Recent Companies</span>
                <a href="#" style={{ fontSize: '13px', color: '#3b82f6' }}>View All</a>
              </div>
            }
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: 'none'
            }}
          >
            <Table
              dataSource={recentCompanies}
              columns={recentColumns}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'No companies yet. Add your first company!' }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Quick Stats</span>
              </div>
            }
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: 'none'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <QuickStatItem 
                label="Active Companies"
                value={stats.activeCompanies}
                color="#10b981"
              />
              <QuickStatItem 
                label="Total Employees"
                value={stats.totalEmployees}
                color="#3b82f6"
              />
              <QuickStatItem 
                label="Expiring Soon"
                value={stats.expiringCompanies}
                color="#f97316"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Helper Components

// MiniChart Component with varied colors
const MiniChart = ({ type }) => {
  const chartData = [
    // Chart 1: Orange-Blue mix
    [
      { height: 40, color: '#ff6b35' },
      { height: 55, color: '#3b82f6' },
      { height: 45, color: '#ff6b35' },
      { height: 65, color: '#3b82f6' },
      { height: 50, color: '#ff6b35' },
      { height: 70, color: '#3b82f6' }
    ],
    // Chart 2: Blue gradient
    [
      { height: 45, color: '#60a5fa' },
      { height: 35, color: '#93c5fd' },
      { height: 50, color: '#3b82f6' },
      { height: 60, color: '#60a5fa' },
      { height: 40, color: '#93c5fd' },
      { height: 55, color: '#3b82f6' }
    ],
    // Chart 3: Green/Teal
    [
      { height: 50, color: '#10b981' },
      { height: 45, color: '#34d399' },
      { height: 55, color: '#10b981' },
      { height: 60, color: '#34d399' },
      { height: 50, color: '#10b981' },
      { height: 65, color: '#34d399' }
    ],
    // Chart 4: Orange gradient
    [
      { height: 40, color: '#fb923c' },
      { height: 50, color: '#f97316' },
      { height: 45, color: '#fb923c' },
      { height: 60, color: '#f97316' },
      { height: 55, color: '#fb923c' },
      { height: 50, color: '#f97316' }
    ],
    // Chart 5: Multi-color
    [
      { height: 45, color: '#ff6b35' },
      { height: 55, color: '#3b82f6' },
      { height: 40, color: '#ff6b35' },
      { height: 60, color: '#3b82f6' },
      { height: 50, color: '#ff6b35' },
      { height: 65, color: '#3b82f6' }
    ],
    // Chart 6: Blue-Purple
    [
      { height: 50, color: '#3b82f6' },
      { height: 45, color: '#60a5fa' },
      { height: 55, color: '#3b82f6' },
      { height: 60, color: '#60a5fa' },
      { height: 40, color: '#3b82f6' },
      { height: 50, color: '#60a5fa' }
    ]
  ];

  const bars = chartData[type % 6];

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'flex-end', 
      justifyContent: 'space-between', 
      height: '100%', 
      gap: '3px' 
    }}>
      {bars.map((bar, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            backgroundColor: bar.color,
            height: `${bar.height}%`,
            borderRadius: '2px 2px 0 0',
            transition: 'all 0.2s ease'
          }}
        />
      ))}
    </div>
  );
};

// CompaniesBarChart Component
const CompaniesBarChart = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const data = [45, 55, 38, 60, 48, 52];

  return (
    <div style={{ 
      height: '180px', 
      display: 'flex', 
      alignItems: 'flex-end', 
      justifyContent: 'space-between', 
      gap: '6px' 
    }}>
      {months.map((month, i) => (
        <div 
          key={i} 
          style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '6px' 
          }}
        >
          <div style={{ 
            width: '100%',
            height: `${data[i] * 2}px`,
            background: 'linear-gradient(to top, #1e293b 0%, #1e293b 33%, #ff6b35 66%, #3b82f6 100%)',
            borderRadius: '3px 3px 0 0',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          />
          <span style={{ fontSize: '10px', color: '#6b7280' }}>{month}</span>
        </div>
      ))}
    </div>
  );
};

// RevenueChart Component with Tooltip
const RevenueChart = ({ stats }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Calculate monthly revenue based on total revenue
  const baseRevenue = stats?.totalRevenue || 67201;
  const generateMonthlyData = () => {
    const monthlyRevenues = [];
    const total = baseRevenue;
    const avgPerMonth = total / 12;
    
    for (let i = 0; i < 12; i++) {
      const variation = (Math.random() - 0.5) * 0.3; // ±15% variation
      monthlyRevenues.push(Math.round(avgPerMonth * (1 + variation)));
    }
    return monthlyRevenues;
  };

  const monthlyRevenues = generateMonthlyData();
  const primaryData = [30, 25, 35, 30, 40, 35, 45, 40, 50, 45, 35, 45];
  const secondaryData = [20, 15, 25, 20, 30, 25, 35, 30, 40, 35, 25, 35];
  const tertiaryData = [10, 8, 15, 12, 18, 15, 22, 18, 25, 20, 15, 20];

  const handleMouseEnter = (index, event) => {
    setHoveredIndex(index);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  return (
    <div style={{ height: '180px', position: 'relative' }}>
      {/* Tooltip */}
      {hoveredIndex !== null && (
        <div style={{
          position: 'fixed',
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#1f2937',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '600',
          zIndex: 1000,
          pointerEvents: 'none',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          whiteSpace: 'nowrap'
        }}>
          <div>{months[hoveredIndex]}</div>
          <div style={{ color: '#10b981', marginTop: '2px' }}>
            ₹{monthlyRevenues[hoveredIndex].toLocaleString()}
          </div>
          <div style={{
            position: 'absolute',
            bottom: '-4px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: '4px solid #1f2937'
          }} />
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        justifyContent: 'space-between', 
        gap: '4px', 
        height: '100%' 
      }}>
        {months.map((month, i) => (
          <div 
            key={i} 
            style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '4px', 
              height: '100%', 
              justifyContent: 'flex-end',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => handleMouseEnter(i, e)}
            onMouseLeave={handleMouseLeave}
          >
            <div style={{ 
              width: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1px',
              opacity: hoveredIndex === null || hoveredIndex === i ? 1 : 0.5,
              transform: hoveredIndex === i ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.2s ease'
            }}>
              <div style={{ 
                width: '100%',
                height: `${primaryData[i] * 1.5}px`,
                backgroundColor: '#3b82f6',
                borderRadius: '2px 2px 0 0'
              }} />
              <div style={{ 
                width: '100%',
                height: `${secondaryData[i] * 1.5}px`,
                backgroundColor: '#ff6b35'
              }} />
              <div style={{ 
                width: '100%',
                height: `${tertiaryData[i] * 1.5}px`,
                backgroundColor: '#1e293b',
                borderRadius: '0 0 2px 2px'
              }} />
            </div>
            <span style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>{month}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// TopPlansDoughnut Component
const TopPlansDoughnut = () => {
  return (
    <div style={{ 
      width: '140px',
      height: '140px',
      margin: '0 auto',
      position: 'relative'
    }}>
      <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#3b82f6"
          strokeWidth="18"
          strokeDasharray="150.8 251.2"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#f97316"
          strokeWidth="18"
          strokeDasharray="50.24 251.2"
          strokeDashoffset="-150.8"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="#eab308"
          strokeWidth="18"
          strokeDasharray="50.24 251.2"
          strokeDashoffset="-201.04"
        />
      </svg>
    </div>
  );
};

// PlanLegend Component
const PlanLegend = ({ color, label, percentage }) => {
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between' 
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div style={{ 
          width: '10px', 
          height: '10px', 
          borderRadius: '50%', 
          backgroundColor: color 
        }} />
        <span style={{ fontSize: '13px', color: '#374151' }}>{label}</span>
      </div>
      <span style={{ fontSize: '13px', fontWeight: '600' }}>{percentage}</span>
    </div>
  );
};

// QuickStatItem Component
const QuickStatItem = ({ label, value, color }) => {
  return (
    <div style={{ 
      padding: '12px',
      backgroundColor: '#f9fafb',
      borderRadius: '6px',
      borderLeft: `3px solid ${color}`
    }}>
      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
